import { useParams } from 'react-router-dom'

import { cn } from 'src/shared/utils/cn'

import type { BCStepName, Block } from '../constants/bc-config'
import useCreateBusinessConfig from '../hooks/use-create-business-config'

const ICON_BOX_SIZE = 55
const ICON_BOX_RADIUS = 15
const PROGRESS_STROKE_WIDTH = 1
const MIN_PROGRESS_AT_ZERO = 12

type BCProgressBarItemProps = {
  block: Block
  currentBlock: number
  currentStepNumber: number
}

const BCProgressBarItem = ({ block, currentBlock, currentStepNumber }: BCProgressBarItemProps) => {
  const wasCompleted = currentBlock > block.blockNumber
  const isCurrent = currentBlock === block.blockNumber
  const percentage = currentStepNumber / block.blockSteps

  const inset = PROGRESS_STROKE_WIDTH / 2
  const progressRectWidth = ICON_BOX_SIZE - PROGRESS_STROKE_WIDTH
  const progressRectHeight = ICON_BOX_SIZE - PROGRESS_STROKE_WIDTH
  const progressRadius = Math.max(ICON_BOX_RADIUS - inset, 0)
  const perimeter = 2 * (progressRectWidth + progressRectHeight - 4 * progressRadius) + 2 * Math.PI * progressRadius
  const topEdgeLength = progressRectWidth - 2 * progressRadius
  const startOffset = topEdgeLength / 2
  const isFullyComplete = wasCompleted || (isCurrent && percentage >= 1)
  const fullProgressLength = perimeter
  const progressLength = wasCompleted
    ? fullProgressLength
    : isCurrent
    ? percentage <= 0
      ? MIN_PROGRESS_AT_ZERO
      : perimeter * Math.min(Math.max(percentage, 0), 1)
    : 0

  return (
    <div
      key={block.key}
      className={cn(
        'relative flex h-[55px] w-[55px] items-center justify-center gap-2 rounded-[15px] border border-white/20 bg-white/10 opacity-50',
        wasCompleted && 'border-white opacity-100',
        isCurrent && 'text-white opacity-100',
      )}
    >
      <svg className="pointer-events-none absolute inset-0" width={ICON_BOX_SIZE} height={ICON_BOX_SIZE}>
        <rect
          x={inset}
          y={inset}
          width={progressRectWidth}
          height={progressRectHeight}
          rx={progressRadius}
          ry={progressRadius}
          fill="none"
          stroke="rgba(255,255,255,0.95)"
          strokeWidth={PROGRESS_STROKE_WIDTH}
          opacity={isFullyComplete ? 1 : 0}
          style={{ transition: 'opacity 140ms ease-out' }}
        />
        <rect
          x={inset}
          y={inset}
          width={progressRectWidth}
          height={progressRectHeight}
          rx={progressRadius}
          ry={progressRadius}
          fill="none"
          stroke="rgba(255,255,255,0.95)"
          strokeWidth={PROGRESS_STROKE_WIDTH}
          strokeDasharray={`${progressLength} ${perimeter}`}
          strokeDashoffset={-startOffset}
          strokeLinecap="butt"
          opacity={isFullyComplete ? 0 : 1}
          style={{
            transition: 'stroke-dasharray 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 140ms ease-out',
          }}
        />
      </svg>
      {block.icon}
    </div>
  )
}

const BCProgressBar = () => {
  const { step } = useParams<{ step: BCStepName }>()
  const { config, blocks } = useCreateBusinessConfig()
  const currentStep = config.steps[step as BCStepName]

  if (!currentStep) {
    return null
  }

  const currentBlock = currentStep.progress.block
  const currentStepNumber = currentStep.progress.step

  return (
    <div className="flex flex-col items-center gap-5">
      {blocks.map((block) => {
        return (
          <BCProgressBarItem
            key={block.key}
            block={block}
            currentBlock={currentBlock}
            currentStepNumber={currentStepNumber}
          />
        )
      })}
    </div>
  )
}

export default BCProgressBar
